// Customer portal Documents page (Session AFH). One place the signed-in customer finds every
// document ELE holds for them: the static Health and Safety and RAMS library (served from disk),
// then their own certificates and other company documents (the AFE library). NO pricing, contract
// value, engineer names or internal notes. A lapsed session (401) sends the customer back to sign
// in. This page is built page-shaped and tested; a later wave routes it (do NOT add a route here).
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import type { PortalComplianceDocGroup, PortalHistoryFeedRow, PortalOverview } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";
import { PortalViewDocumentButton } from "./PortalDocumentViewer";

// The customer-facing page heading for each static compliance category (plainer than the ISO
// labels the server sends, which suit the internal library, not the customer).
const COMPLIANCE_HEADING: Record<string, string> = {
  ohs: "Health and safety",
  rams: "Risk assessments and method statements",
};

export function PortalDocumentsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<PortalOverview | null>(null);
  const [complianceDocs, setComplianceDocs] = useState<PortalComplianceDocGroup[] | null>(null);
  const [error, setError] = useState(false);
  // Session AUD (email system Phase 5, Item 3): the customer's APPROVED condition reports, read from the
  // history feed's own section. A separate read on purpose: if it fails the rest of the page still
  // renders, and the section is simply not shown.
  const [conditionReports, setConditionReports] = useState<PortalHistoryFeedRow[]>([]);

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => api.portal.history())
      .then((h) => {
        if (live) setConditionReports((h?.conditionReports ?? []).filter((r) => r.reportAvailable && r.jobId));
      })
      .catch(() => {
        // The section stays hidden; the rest of the page is unaffected.
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    Promise.all([api.portal.overview(), api.portal.complianceDocs()])
      .then(([o, docs]) => {
        if (!live) return;
        setOverview(o);
        setComplianceDocs(docs);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!overview || !complianceDocs) return <FullScreenLoader />;

  // Only categories with at least one document render (an empty heading serves nobody).
  const complianceGroups = complianceDocs.filter((g) => g.documents.length > 0);
  const companyGroups = overview.companyDocuments ?? [];

  return (
    <PortalShell wide userName={overview.customerName} onSignOut={logout}>
      <header className="mb-6">
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">Documents</h1>
        <p className="mt-1 text-ele-body">
          Your health and safety documents, method statements, certificates and other paperwork.
        </p>
      </header>

      {complianceGroups.map((group) => (
        <Section key={group.category} title={COMPLIANCE_HEADING[group.category] ?? group.label}>
          <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
            {group.documents.map((doc) => (
              <li
                key={doc.filename}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <FileText aria-hidden size={16} className="flex-none" color="#2CB9F3" />
                  <span className="text-sm text-ele-white">{doc.displayName}</span>
                  <span className="rounded border border-white/15 px-1.5 py-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-body">
                    {doc.kind}
                  </span>
                </div>
                {/* View only for a PDF. The static library also serves .docx (11 of the 12 rows today),
                    and a Word document in an iframe is a blank panel: the viewer would be worse than
                    the download link it replaced. A .docx keeps Download, which is the only thing a
                    browser can usefully do with it. */}
                {doc.kind === "PDF" ? (
                  <PortalViewDocumentButton
                    href={api.portal.complianceDocHref(group.category, doc.filename)}
                    title={doc.displayName}
                  />
                ) : (
                  <a
                    href={api.portal.complianceDocHref(group.category, doc.filename)}
                    className="min-h-11 font-menu text-xs tracking-wide text-ele-green hover:underline"
                  >
                    Download
                  </a>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ))}

      {/* Certificates and other company documents (Session AFE): the customer's own uploaded
          documents (insurance certificates, COSHH, supplementary-test annexes), grouped by
          category exactly as the dashboard renders them. Hidden entirely when there are none. */}
      {companyGroups.length > 0 && (
        <Section title="Certificates and other documents">
          <div className="space-y-4">
            {companyGroups.map((group) => (
              <div key={group.category}>
                <h3 className="mb-2 font-menu text-xs uppercase tracking-wider text-ele-body">{group.categoryLabel}</h3>
                <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
                  {group.documents.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <FileText aria-hidden size={16} className="mt-0.5 flex-none" color="#2CB9F3" />
                        <div>
                        <p className="text-sm text-ele-white">{doc.label}</p>
                        {doc.validUntilDisplay && <p className="text-xs text-ele-body">Valid to: {doc.validUntilDisplay}</p>}
                        </div>
                      </div>
                      <PortalViewDocumentButton href={api.portal.companyDocumentHref(doc.id)} title={doc.label} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Session AUD (Item 3): condition reports the office has approved, the document the customer was
          emailed, opened in the portal viewer. Hidden when there are none. */}
      {conditionReports.length > 0 && (
        <Section title="Condition reports">
          <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
            {conditionReports.map((r) => (
              <li
                key={r.jobId!}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <FileText aria-hidden size={16} className="mt-0.5 flex-none" color="#2CB9F3" />
                  <div>
                    <p className="text-sm text-ele-white">
                      {r.siteName} <span className="text-ele-body">· {r.liftRef}</span>
                    </p>
                    <p className="text-xs text-ele-body">{r.dateDisplay}</p>
                  </div>
                </div>
                <PortalViewDocumentButton href={api.portal.jobReportHref(r.jobId!)} title={`Condition report, ${r.liftRef}, ${r.dateDisplay}`} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {complianceGroups.length === 0 && companyGroups.length === 0 && conditionReports.length === 0 && (
        <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
          No documents are available yet. If you need a document, call the office on{" "}
          <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
        </p>
      )}
    </PortalShell>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 font-heading text-xl text-ele-purple">{title}</h2>
      {children}
    </section>
  );
}
